export type RemoteCursor = {
  userId: string;
  seat: number;
  color: string;
  x: number;
  y: number;
  username?: string;
};

export type PresenceMember = {
  userId: string;
  username: string;
  seat: number;
  color: string;
};

export type LobbyMatch = {
  instanceId: string;
  peer: {userId: string; username: string};
};

export type InstanceHandlers = {
  onStatus: (msg: string) => void;
  onWelcome: (info: {seat: number; color: string; instanceId: string}) => void;
  onCursor: (cursor: RemoteCursor) => void;
  onPresence: (members: PresenceMember[]) => void;
  onMatched: (match: LobbyMatch) => void;
};

export type InstanceSession = {
  readonly connected: boolean;
  readonly seat: number;
  readonly color: string;
  sendCursor(x: number, y: number): void;
  queueMatch(): void;
  cancelMatch(): void;
  close(): void;
};

export function connectInstance(
  opts: {instanceId: string; userId: string; username: string; accessToken?: string},
  handlers: InstanceHandlers
): InstanceSession | undefined {
  const url = import.meta.env.VITE_WS_URL as string | undefined;
  if (!url) {
    handlers.onStatus('Local play (relay disabled).');
    return undefined;
  }

  let connected = false;
  let seat = 0;
  let color = '#cfd8dc';
  let lastSent = 0;
  let lastX = -2;
  let lastY = -2;

  const socket = new WebSocket(url);

  socket.addEventListener('open', () => {
    socket.send(
      JSON.stringify({
        type: 'join',
        instanceId: opts.instanceId,
        userId: opts.userId,
        username: opts.username,
        accessToken: opts.accessToken
      })
    );
  });

  socket.addEventListener('message', event => {
    let msg: {type: string; [key: string]: unknown};
    try {
      msg = JSON.parse(String(event.data));
    } catch {
      return;
    }

    if (msg.type === 'welcome') {
      connected = true;
      seat = Number(msg.seat ?? 0);
      color = String(msg.color ?? '#cfd8dc');
      const instanceId = String(msg.instanceId ?? opts.instanceId);
      handlers.onWelcome({seat, color, instanceId});
      handlers.onStatus(`Connected as P${seat + 1}.`);
      return;
    }

    if (msg.type === 'presence') {
      const members = (msg.members as PresenceMember[]) ?? [];
      handlers.onPresence(members);
      return;
    }

    if (msg.type === 'cursor') {
      handlers.onCursor({
        userId: String(msg.userId),
        seat: Number(msg.seat ?? 0),
        color: String(msg.color ?? '#cfd8dc'),
        x: Number(msg.x),
        y: Number(msg.y),
        username: msg.username as string | undefined
      });
      return;
    }

    if (msg.type === 'lobby-matched') {
      handlers.onMatched({
        instanceId: String(msg.instanceId),
        peer: msg.peer as LobbyMatch['peer']
      });
      return;
    }

    if (msg.type === 'error') {
      handlers.onStatus(`Relay rejected: ${msg.reason ?? 'unknown'}`);
    }
  });

  socket.addEventListener('close', () => {
    connected = false;
    handlers.onStatus('Relay disconnected; continuing locally.');
  });

  socket.addEventListener('error', () => {
    handlers.onStatus('Relay unreachable; continuing locally.');
  });

  return {
    get connected() {
      return connected;
    },
    get seat() {
      return seat;
    },
    get color() {
      return color;
    },
    sendCursor(x, y) {
      if (!connected || socket.readyState !== WebSocket.OPEN) return;
      const now = performance.now();
      if (now - lastSent < 33) return;
      if (Math.abs(x - lastX) < 0.002 && Math.abs(y - lastY) < 0.002) return;
      lastSent = now;
      lastX = x;
      lastY = y;
      socket.send(JSON.stringify({type: 'cursor', x, y}));
    },
    queueMatch() {
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({type: 'lobby-queue', userId: opts.userId, username: opts.username}));
      }
    },
    cancelMatch() {
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({type: 'lobby-cancel'}));
      }
    },
    close() {
      socket.close();
    }
  };
}
